import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { dropdownNaturalScrollClassName, stopDropdownScrollPropagation } from "@/lib/dropdownNaturalScroll";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface MonthYearPickerProps {
  value?: string; // "YYYY-MM" or "YYYY-MM-DD"
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  minYear?: number;
  maxYear?: number;
  showPresent?: boolean;
  isPresent?: boolean;
  onPresentChange?: (present: boolean) => void;
}

export function MonthYearPicker({
  value,
  onChange,
  disabled,
  className,
  placeholder,
  minYear = 1970,
  maxYear,
  showPresent,
  isPresent,
  onPresentChange,
}: MonthYearPickerProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const effectiveMaxYear = maxYear ?? currentYear + 5;
  const monthLabels = t("seek.cvDiff.months", { returnObjects: true }) as string[];
  const localizedMonths = Array.isArray(monthLabels) && monthLabels.length === 12 ? monthLabels : MONTHS;

  const parsed = useMemo(() => {
    if (!value) return { year: null as number | null, month: null as number | null };
    const parts = value.split("-");
    return {
      year: parseInt(parts[0], 10) || null,
      month: parts[1] ? parseInt(parts[1], 10) : null,
    };
  }, [value]);

  const [selectedYear, setSelectedYear] = useState<number | null>(parsed.year);
  const [open, setOpen] = useState(false);

  // Sync when value changes externally
  React.useEffect(() => {
    if (parsed.year) setSelectedYear(parsed.year);
  }, [parsed.year]);

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = effectiveMaxYear; y >= minYear; y--) arr.push(y);
    return arr;
  }, [minYear, effectiveMaxYear]);

  const handleMonthClick = (monthIndex: number) => {
    if (!selectedYear) return;
    const mm = String(monthIndex + 1).padStart(2, "0");
    onChange(`${selectedYear}-${mm}`);
    setOpen(false);
  };

  const displayValue = useMemo(() => {
    if (!parsed.year || !parsed.month) return null;
    return `${localizedMonths[parsed.month - 1]} ${parsed.year}`;
  }, [localizedMonths, parsed]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-start font-normal h-10",
            !displayValue && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <CalendarIcon className="me-2 h-4 w-4 shrink-0" />
          {isPresent ? t("seek.cvDiff.present") : (displayValue || placeholder || t("seek.cvDiff.selectMonthYear"))}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 pointer-events-auto" align="start">
        <div className="flex h-[260px]">
          {/* Year list */}
          <div
            className={cn("w-[90px] border-e", dropdownNaturalScrollClassName)}
            onWheel={stopDropdownScrollPropagation}
            onTouchMove={stopDropdownScrollPropagation}
          >
            <div className="p-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-2">{t("seek.cvDiff.year")}</div>
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setSelectedYear(y)}
                className={cn(
                  "w-full text-start px-3 py-1.5 text-sm hover:bg-accent transition-colors",
                  selectedYear === y && "bg-seek text-white font-medium"
                )}
              >
                {y}
              </button>
            ))}
          </div>
          {/* Month grid */}
          <div className="flex-1 p-3">
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {selectedYear ? t("seek.cvDiff.monthWithYear", { year: selectedYear }) : t("seek.cvDiff.selectYearFirst")}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {localizedMonths.map((m, i) => (
                <button
                  key={m}
                  disabled={!selectedYear}
                  onClick={() => handleMonthClick(i)}
                  className={cn(
                    "rounded-md py-2 text-sm transition-colors",
                    !selectedYear && "opacity-30 cursor-not-allowed",
                    selectedYear && "hover:bg-accent",
                    parsed.year === selectedYear && parsed.month === i + 1
                      ? "bg-seek text-white font-medium"
                      : ""
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
            {showPresent && onPresentChange && (
              <div className="mt-3 pt-3 border-t">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!isPresent}
                    onChange={(e) => {
                      onPresentChange(e.target.checked);
                      if (e.target.checked) setOpen(false);
                    }}
                    className="accent-[hsl(var(--seek))] w-3.5 h-3.5 rounded"
                  />
                  <span className="text-xs font-medium text-muted-foreground">{t("seek.cvDiff.presentCurrentRole")}</span>
                </label>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
